import { createContext, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import QueryApi from 'src/shared/api/query-api';
import axios from 'axios';
interface TagsContextType {
	tagsData: Array<{ label: string; icon: string; value: string }>;
	flags: Array<{ name: string; flagImg: string }>;
	formik: any;
	handleFlagSelection: (flagName: string) => Promise<void>;
	handleTagSelection: (value: string) => Promise<void>;
	allowTagClick: boolean;
}
export const TagsContext = createContext<TagsContextType | undefined>(undefined);

interface Props {
	children: React.ReactNode;
}

export const TagsProvider = ({ children }: Props) => {
	const formik = useFormik({
		initialValues: {
			selectedTags: [] as string[],
			selectedFlag: '',
			search: '',
		},
		validationSchema: yup.object({
			selectedTags: yup.array().of(yup.string()).max(3, 'You can select up to 3 tags'),
		}),
		onSubmit: (values) => {
			console.log('Form values:', values);
		},
	});

	const [tagsData] = useState([
		{
			label: 'NFTs',
			icon: '🖼️', // Emoji for NFTs
			value: 'NFTs',
			metadata: {
				withdrawlPenaltyTime: '',
				withdrawalPenalty: '',
			},
		},
		{
			label: 'DeFi',
			icon: '📈', // Emoji for DeFi
			value: 'DeFi',
			metadata: {
				withdrawlPenaltyTime: '',
				withdrawalPenalty: '',
			},
		},
		{
			label: 'GameFi',
			icon: '👾', // Emoji for GameFi
			value: 'GameFi',
			metadata: {
				withdrawlPenaltyTime: '',
				withdrawalPenalty: '',
			},
		},
		{
			label: 'Tokenomics',
			icon: '⚖️', // Emoji for Tokenomics
			value: 'Tokenomics',
			metadata: {
				withdrawlPenaltyTime: '',
				withdrawalPenalty: '',
			},
		},
		{
			label: 'Researcher',
			icon: '💡', // Emoji for Researcher
			value: 'Researcher',
			metadata: {
				withdrawlPenaltyTime: '',
				withdrawalPenalty: '',
			},
		},
		{
			label: 'Memes',
			icon: '😄', // Emoji for Memes
			value: 'Memes',
			metadata: {
				withdrawlPenaltyTime: '',
				withdrawalPenalty: '',
			},
		},
		{
			label: 'Trading',
			icon: '🔄', // Emoji for Trading
			value: 'Trading',
			metadata: {
				withdrawlPenaltyTime: '',
				withdrawalPenalty: '',
			},
		},
		{
			label: 'Auditor',
			icon: '🔍', // Emoji for Auditor
			value: 'Auditor',
			metadata: {
				withdrawlPenaltyTime: '',
				withdrawalPenalty: '',
			},
		},
		{
			label: 'Developer',
			icon: '💻', // Emoji for Developer
			value: 'Developer',
			metadata: {
				withdrawlPenaltyTime: '',
				withdrawalPenalty: '',
			},
		},
		{
			label: 'Governance',
			icon: '🌐', // Emoji for Governance
			value: 'Governance',
			metadata: {
				withdrawlPenaltyTime: '',
				withdrawalPenalty: '',
			},
		},
		{
			label: 'Designer',
			icon: '✏️', // Emoji for Designer
			value: 'Designer',
			metadata: {
				designer: '',
			},
		},
		{
			label: 'Community',
			icon: '👥', // Emoji for Community
			value: 'Community',
			metadata: {
				Community: '',
			},
		},
	]);
	const [flags, setFlags] = useState<Array<{ name: string; flagImg: string }>>([]);

	useEffect(() => {
		const fetchFlags = async () => {
			try {
				const response = await axios.get('https://restcountries.com/v3.1/all');
				const flagsData = response.data.map((country: any) => ({
					name: country.name.common,
					flagImg: country.flags.svg || country.flags.png, // Prefer SVG, fallback to PNG
				}));
				setFlags(flagsData);
			} catch (error) {
				console.error('Error fetching country flags:', error);
			}
		};

		fetchFlags();
	}, []);

	if (!formik || !formik.values) {
		return null; // or a loading state, if appropriate
	}

	const handleFlagSelection = async (flagName: string) => {
		const currentFlag = formik.values.selectedFlag;

		if (currentFlag === flagName) {
			// If the selected flag is already the selected one, remove it
			try {
				await QueryApi.user.deleteFlag(); // Call the API to remove the flag
				formik.setFieldValue('selectedFlag', '');
			} catch (error) {
				console.error('Error removing flag:', error);
			}
		} else {
			// If it's a new flag, update it
			try {
				await QueryApi.user.addOrUpdateFlag(flagName); // Call the API to update the flag
				formik.setFieldValue('selectedFlag', flagName);
			} catch (error) {
				console.error('Error updating flag:', error);
			}
		}
	};

	const [allowTagClick, setAllowTagClick] = useState(true);
	const handleTagSelection = async (value: string) => {
		const currentTags = formik.values.selectedTags;
		const isSelected = currentTags.includes(value);

		if (isSelected) {
			// Remove the selected tag
			const updatedTags = currentTags.filter((tag) => tag !== value);
			try {
				await QueryApi.user.addOrUpdateInterests(updatedTags); // Update the backend with the new list of tags
				formik.setFieldValue('selectedTags', updatedTags);
				setAllowTagClick(false);
			} catch (error) {
				console.error('Error updating tags:', error);
			}
		} else if (currentTags.length < 3) {
			// Add the selected tag (limit to 3 tags)
			const updatedTags = [...currentTags, value];
			try {
				await QueryApi.user.addOrUpdateInterests(updatedTags); // Update the backend with the new list of tags
				formik.setFieldValue('selectedTags', updatedTags);
				setAllowTagClick(false);
			} catch (error) {
				console.error('Error updating tags:', error);
			}
		}
	};

	return (
		<TagsContext.Provider
			value={{
				tagsData,
				flags,
				formik,
				handleFlagSelection,
				handleTagSelection,
				allowTagClick,
			}}
		>
			{children}
		</TagsContext.Provider>
	);
};
